import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    TrendingUp,
    TrendingDown,
    DollarSign,
    Users,
    ShoppingBag,
    Gavel,
    Eye,
    AlertTriangle,
    BarChart3,
    Activity,
    Clock,
    ChevronRight,
    ArrowUpRight,
    ArrowDownRight,
    CheckCircle
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#3395FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const MetricCard = ({ title, value, change, icon: Icon, trend, description, loading }) => {
    if (loading) {
        return (
            <Card>
                <CardContent className="p-4">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-32 mb-1" />
                    <Skeleton className="h-3 w-20" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{title}</span>
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                    </div>
                </div>
                <div className="text-2xl font-bold mb-1">{value}</div>
                {change !== undefined && (
                    <div className={`flex items-center text-xs ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {trend === 'up' ? <ArrowUpRight className="w-3 h-3 mr-1" /> : trend === 'down' ? <ArrowDownRight className="w-3 h-3 mr-1" /> : null}
                        {change > 0 ? '+' : ''}{change.toFixed(1)}% vs perioada anterioară
                    </div>
                )}
                {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
            </CardContent>
        </Card>
    );
};

const AdminReportsTab = ({ totals = {} }) => {
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30d');
    const [overview, setOverview] = useState(null);
    const [trends, setTrends] = useState(null);
    const [selectedMetric, setSelectedMetric] = useState('revenue');
    const [userBehavior, setUserBehavior] = useState(null);

    useEffect(() => {
        fetchReports();
    }, [period]);

    useEffect(() => {
        if (overview) {
            fetchTrends();
        }
    }, [selectedMetric, period]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const [overviewRes, behaviorRes] = await Promise.all([
                api.get(`/admin/reports/overview?period=${period}`),
                api.get('/admin/reports/user-behavior')
            ]);
            setOverview(overviewRes.data);
            setUserBehavior(behaviorRes.data);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrends = async () => {
        try {
            const res = await api.get(`/admin/reports/trends?metric=${selectedMetric}&period=${period}`);
            setTrends(res.data);
        } catch (error) {
            console.error('Error fetching trends:', error);
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border">
                    <p className="font-medium text-sm">{label}</p>
                    {payload.map((p, i) => (
                        <p key={i} className="text-sm" style={{ color: p.color }}>
                            {p.name}: {p.name.includes('RON') || p.dataKey.includes('amount') 
                                ? formatCurrency(p.value) 
                                : p.value.toLocaleString()}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6" data-testid="admin-reports-tab">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold">Rapoarte & Analytics</h2>
                    <p className="text-sm text-muted-foreground">Analizează performanța platformei</p>
                </div>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-[140px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7d">Ultimele 7 zile</SelectItem>
                        <SelectItem value="30d">Ultimele 30 zile</SelectItem>
                        <SelectItem value="90d">Ultimele 90 zile</SelectItem>
                        <SelectItem value="1y">Ultimul an</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Completed Listings Stats */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Anunțuri Finalizate</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Gavel className="w-8 h-8 text-emerald-500" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-emerald-800">{totals.sold_auctions || 0}</p>
                            <p className="text-sm text-emerald-600">Licitații finalizate</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-teal-50 border-teal-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <ShoppingBag className="w-8 h-8 text-teal-500" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-teal-800">{totals.sold_sales || 0}</p>
                            <p className="text-sm text-teal-600">Vânzări finalizate</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-orange-50 border-orange-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Clock className="w-8 h-8 text-orange-500" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-orange-800">{totals.open_orders || 0}</p>
                            <p className="text-sm text-orange-600">Comenzi deschise</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-sky-50 border-sky-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Gavel className="w-8 h-8 text-sky-500" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-sky-800">{totals.active_auctions || 0}</p>
                            <p className="text-sm text-sky-600">Licitații deschise</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Active Listings Values */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Valoare Anunțuri Deschise</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-amber-600 mb-1">Valoare Vânzări Deschise</p>
                                    <p className="text-2xl font-bold text-amber-800">{formatCurrency(totals.active_sales_value || 0)}</p>
                                    <p className="text-xs text-amber-500 mt-1">{totals.active_sales || 0} anunțuri active</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                    <ShoppingBag className="w-6 h-6 text-amber-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-violet-50 border-violet-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-violet-600 mb-1">Valoare Licitații Deschise</p>
                                    <p className="text-2xl font-bold text-violet-800">{formatCurrency(totals.active_auctions_value || 0)}</p>
                                    <p className="text-xs text-violet-500 mt-1">{totals.active_auctions || 0} licitații active</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                                    <Gavel className="w-6 h-6 text-violet-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Valoare Vânzări Încheiate */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Valoare Vânzări Încheiate</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-emerald-600 mb-1">Valoare Vânzări Finalizate</p>
                                    <p className="text-2xl font-bold text-emerald-800">{formatCurrency(totals.completed_sales_value || overview?.revenue?.gmv || 0)}</p>
                                    <p className="text-xs text-emerald-500 mt-1">{totals.completed_sales || overview?.revenue?.total_orders || 0} tranzacții</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-blue-600 mb-1">Valoare Licitații Câștigate</p>
                                    <p className="text-2xl font-bold text-blue-800">{formatCurrency(totals.completed_auctions_value || 0)}</p>
                                    <p className="text-xs text-blue-500 mt-1">{totals.completed_auctions || 0} licitații încheiate</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                                    <Gavel className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                    title="Venituri Platformă"
                    value={formatCurrency(overview?.revenue?.platform_revenue || 0)}
                    icon={DollarSign}
                    loading={loading}
                    description={`GMV: ${formatCurrency(overview?.revenue?.gmv || 0)}`}
                />
                <MetricCard
                    title="Utilizatori Noi"
                    value={overview?.users?.new_users || 0}
                    icon={Users}
                    loading={loading}
                    description={`Total: ${overview?.users?.total_users || 0}`}
                />
                <MetricCard
                    title="Comenzi"
                    value={overview?.revenue?.total_orders || 0}
                    icon={ShoppingBag}
                    loading={loading}
                    description={`Valoare medie: ${formatCurrency(overview?.revenue?.avg_order_value || 0)}`}
                />
                <MetricCard
                    title="Rată Conversie"
                    value={`${overview?.conversion?.conversion_rate || 0}%`}
                    icon={Eye}
                    loading={loading}
                    description={`${overview?.conversion?.total_views || 0} vizualizări`}
                />
            </div>

            {/* Auction Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                    title="Licitații"
                    value={overview?.auctions?.total || 0}
                    icon={Gavel}
                    loading={loading}
                    description={`Finalizate: ${overview?.auctions?.completed || 0}`}
                />
                <MetricCard
                    title="Rată Finalizare"
                    value={`${overview?.auctions?.completion_rate || 0}%`}
                    icon={TrendingUp}
                    loading={loading}
                />
                <MetricCard
                    title="Oferte/Licitație"
                    value={overview?.auctions?.avg_bids_per_auction?.toFixed(1) || '0'}
                    icon={Activity}
                    loading={loading}
                />
                <MetricCard
                    title="Dispute"
                    value={overview?.disputes?.total || 0}
                    icon={AlertTriangle}
                    loading={loading}
                    description={`Rată: ${overview?.disputes?.dispute_rate || 0}%`}
                />
            </div>

            {/* Trends Chart */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <CardTitle>Tendințe</CardTitle>
                            <CardDescription>Evoluția metricilor în timp</CardDescription>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {[
                                { key: 'revenue', label: 'Venituri' },
                                { key: 'gmv', label: 'GMV' },
                                { key: 'orders', label: 'Comenzi' },
                                { key: 'users', label: 'Utilizatori' },
                                { key: 'listings', label: 'Listări' },
                                { key: 'bids', label: 'Oferte' }
                            ].map(m => (
                                <Button
                                    key={m.key}
                                    variant={selectedMetric === m.key ? 'default' : 'outline-solid'}
                                    size="sm"
                                    onClick={() => setSelectedMetric(m.key)}
                                    className="text-xs"
                                >
                                    {m.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {trends ? (
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-muted-foreground">Total:</span>
                                <span className="font-bold">
                                    {selectedMetric === 'revenue' || selectedMetric === 'gmv' 
                                        ? formatCurrency(trends.total) 
                                        : trends.total.toLocaleString()}
                                </span>
                                <span className="text-muted-foreground">|</span>
                                <span className="text-muted-foreground">Media zilnică:</span>
                                <span className="font-medium">
                                    {selectedMetric === 'revenue' || selectedMetric === 'gmv'
                                        ? formatCurrency(trends.average)
                                        : Math.round(trends.average).toLocaleString()}
                                </span>
                                {trends.growth_rate !== 0 && (
                                    <>
                                        <span className="text-muted-foreground">|</span>
                                        <span className={`flex items-center ${trends.growth_rate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {trends.growth_rate > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                                            {trends.growth_rate > 0 ? '+' : ''}{trends.growth_rate}%
                                        </span>
                                    </>
                                )}
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={trends.data}>
                                    <defs>
                                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3395FF" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#3395FF" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area 
                                        type="monotone" 
                                        dataKey="value" 
                                        stroke="#3395FF" 
                                        strokeWidth={2}
                                        fill="url(#colorValue)" 
                                        name={selectedMetric === 'revenue' || selectedMetric === 'gmv' ? 'RON' : 'Valoare'}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <Skeleton className="h-[300px] w-full" />
                    )}
                </CardContent>
            </Card>

            {/* Category Performance & Top Sellers */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Category Performance */}
                <Card>
                    <CardHeader>
                        <CardTitle>Performanță Categorii</CardTitle>
                        <CardDescription>Vânzări per categorie</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {overview?.category_performance?.length > 0 ? (
                            <div className="space-y-3">
                                {overview.category_performance.map((cat, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-3 h-3 rounded-full" 
                                                style={{ backgroundColor: COLORS[i % COLORS.length] }}
                                            />
                                            <div>
                                                <p className="font-medium text-sm">{cat._id || 'Necunoscut'}</p>
                                                <p className="text-xs text-muted-foreground">{cat.order_count} comenzi</p>
                                            </div>
                                        </div>
                                        <span className="font-bold">{formatCurrency(cat.total_sales)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-muted-foreground py-8">Nu există date</p>
                        )}
                    </CardContent>
                </Card>

                {/* Top Sellers */}
                <Card>
                    <CardHeader>
                        <CardTitle>Top Vânzători</CardTitle>
                        <CardDescription>Cei mai activi vânzători</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {overview?.top_sellers?.length > 0 ? (
                            <div className="space-y-3">
                                {overview.top_sellers.slice(0, 5).map((seller, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-sm text-primary">
                                                {i + 1}
                                            </div>
                                            <div>
                                                <p className="font-medium text-sm">{seller.name || seller.username}</p>
                                                <p className="text-xs text-muted-foreground">{seller.order_count} vânzări</p>
                                            </div>
                                        </div>
                                        <span className="font-bold">{formatCurrency(seller.total_sales)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-muted-foreground py-8">Nu există date</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* User Behavior Analytics */}
            <Card>
                <CardHeader>
                    <CardTitle>Comportament Utilizatori</CardTitle>
                    <CardDescription>Analiza activității și paternurilor de utilizare</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Activity by Hour */}
                        <div>
                            <h4 className="font-medium mb-3 text-sm">Activitate pe Ore (azi)</h4>
                            {userBehavior?.activity_by_hour?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={userBehavior.activity_by_hour}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="count" fill="#3395FF" radius={[4, 4, 0, 0]} name="Activități" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                                    Nu există date
                                </div>
                            )}
                        </div>

                        {/* Browser Distribution */}
                        <div>
                            <h4 className="font-medium mb-3 text-sm">Browsere Utilizate</h4>
                            {userBehavior?.browsers?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={userBehavior.browsers}
                                            dataKey="count"
                                            nameKey="browser"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={70}
                                            label={({ browser, percent }) => `${browser} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {userBehavior.browsers.map((entry, i) => (
                                                <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                                    Nu există date
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Distribution */}
                    {userBehavior?.action_distribution?.length > 0 && (
                        <div className="mt-6">
                            <h4 className="font-medium mb-3 text-sm">Distribuția Acțiunilor</h4>
                            <div className="flex flex-wrap gap-2">
                                {userBehavior.action_distribution.slice(0, 10).map((action, i) => (
                                    <Badge key={i} variant="outline" className="px-3 py-1">
                                        {action.action}: <span className="font-bold ml-1">{action.count}</span>
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminReportsTab;
